// OrderSelfship.jsx - Self-Ship Tracking Modal (Insert Only)
// API calls handled by parent via onSubmit prop

import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { courierListOrder } from '../../../utilities/courierList';
import './OrderSelfship.css';

const OTHERS_VALUE = 'OTHERS';

function OrderSelfship({ show, handleClose, orderData, onSubmit, onSuccess }) {
  const [carrier, setCarrier] = useState('');
  const [otherCourierName, setOtherCourierName] = useState('');
  const [noOfDockets, setNoOfDockets] = useState(1);
  const [trackingIds, setTrackingIds] = useState(['']);
  const [trackingLinks, setTrackingLinks] = useState(['']);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const isOthers = carrier === OTHERS_VALUE;

  useEffect(() => {
    if (show && orderData) {
      console.log('📦 OrderSelfship Modal Opened:', orderData);
      setCarrier('');
      setOtherCourierName('');
      setNoOfDockets(1);
      setTrackingIds(['']);
      setTrackingLinks(['']);
      setErrors({});
      setSubmitSuccess(false);
    }
  }, [show, orderData]);

  const handleCourierChange = (value) => {
    setCarrier(value);
    if (value !== OTHERS_VALUE) {
      setOtherCourierName('');
    }
    if (errors.carrier) {
      const newErrors = { ...errors };
      delete newErrors.carrier;
      setErrors(newErrors);
    }
  };

  const handleOtherCourierNameChange = (value) => {
    setOtherCourierName(value);
    if (errors.otherCourierName) {
      const newErrors = { ...errors };
      delete newErrors.otherCourierName;
      setErrors(newErrors);
    }
  };

  const handleNoOfDocketsChange = (value) => {
    const numValue = parseInt(value) || 0;
    if (numValue > 20) return;

    setNoOfDockets(numValue);

    // Sync trackingIds array length
    const currentTrackingIds = [...trackingIds];
    if (numValue > currentTrackingIds.length) {
      const newArr = [...currentTrackingIds];
      for (let i = currentTrackingIds.length; i < numValue; i++) newArr.push('');
      setTrackingIds(newArr);
    } else if (numValue < currentTrackingIds.length && numValue > 0) {
      setTrackingIds(currentTrackingIds.slice(0, numValue));
    }

    // Sync trackingLinks array length
    const currentTrackingLinks = [...trackingLinks];
    if (numValue > currentTrackingLinks.length) {
      const newArr = [...currentTrackingLinks];
      for (let i = currentTrackingLinks.length; i < numValue; i++) newArr.push('');
      setTrackingLinks(newArr);
    } else if (numValue < currentTrackingLinks.length && numValue > 0) {
      setTrackingLinks(currentTrackingLinks.slice(0, numValue));
    }

    if (errors.noOfDockets) {
      const newErrors = { ...errors };
      delete newErrors.noOfDockets;
      setErrors(newErrors);
    }
  };

  const handleTrackingIdChange = (index, value) => {
    const updated = [...trackingIds];
    updated[index] = value.trim();
    setTrackingIds(updated);
    if (errors[`trackingId_${index}`]) {
      const newErrors = { ...errors };
      delete newErrors[`trackingId_${index}`];
      setErrors(newErrors);
    }
  };

  const handleTrackingLinkChange = (index, value) => {
    const updated = [...trackingLinks];
    updated[index] = value.trim();
    setTrackingLinks(updated);
    if (errors[`trackingLink_${index}`]) {
      const newErrors = { ...errors };
      delete newErrors[`trackingLink_${index}`];
      setErrors(newErrors);
    }
  };

  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    if (!carrier || carrier.trim() === '') {
      newErrors.carrier = 'Courier is required';
      isValid = false;
    }

    if (isOthers) {
      if (!otherCourierName || otherCourierName.trim() === '') {
        newErrors.otherCourierName = 'Courier name is required';
        isValid = false;
      }
    }

    if (!noOfDockets || noOfDockets < 1) {
      newErrors.noOfDockets = 'At least 1 docket required';
      isValid = false;
    }

    trackingIds.forEach((trackingId, index) => {
      if (!trackingId || trackingId.trim() === '') {
        newErrors[`trackingId_${index}`] = 'Tracking ID required';
        isValid = false;
      } else if (trackingId.trim().length < 5) {
        newErrors[`trackingId_${index}`] = 'Invalid tracking ID (min 5 characters)';
        isValid = false;
      }
    });

    // Tracking links are mandatory for OTHERS; optional but validated if filled for others
    trackingLinks.forEach((link, index) => {
      if (isOthers) {
        if (!link || link.trim() === '') {
          newErrors[`trackingLink_${index}`] = 'Tracking link is required';
          isValid = false;
        } else if (!isValidUrl(link.trim())) {
          newErrors[`trackingLink_${index}`] = 'Enter a valid URL (e.g. https://track.courier.com/...)';
          isValid = false;
        }
      } else if (link && link.trim() !== '' && !isValidUrl(link.trim())) {
        newErrors[`trackingLink_${index}`] = 'Enter a valid URL (e.g. https://track.courier.com/...)';
        isValid = false;
      }
    });

    const trimmedIds = trackingIds.map(id => id.trim().toUpperCase()).filter(id => id);
    const duplicates = trimmedIds.filter((id, idx) => trimmedIds.indexOf(id) !== idx);
    if (duplicates.length > 0) {
      newErrors.general = `Duplicate tracking IDs: ${[...new Set(duplicates)].join(', ')}`;
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      // Build per-docket tracking entries
      const docketEntries = trackingIds
        .filter(id => id.trim() !== '')
        .map((id, index) => ({
          trackingId: id.trim(),
          trackingLink: trackingLinks[index]?.trim() || null,
        }));

      const payload = {
        orderId: orderData.orderId,
        productIndex: orderData.productIndex || 0,
        packages: [{
          packageNo: 1,
          carrier: isOthers ? OTHERS_VALUE : carrier,
          otherCourierName: isOthers ? otherCourierName.trim() : null,
          noOfDockets: noOfDockets,
          trackingIds: docketEntries.map(d => d.trackingId),
          trackingLinks: docketEntries.map(d => d.trackingLink),
        }],
        isUpdate: false
      };

      console.log('📤 Submitting Payload:', payload);
      const result = await onSubmit(payload);
      console.log('✅ Submit Result:', result);

      setSubmitSuccess(true);

      if (onSuccess) {
        onSuccess(result);
      }

      setTimeout(() => {
        handleModalClose();
      }, 1500);

    } catch (error) {
      console.error('❌ Submit Error:', error);
      setErrors({
        submit: error.response?.data?.message || error.message || 'Failed to submit. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalClose = () => {
    if (!isLoading) {
      setCarrier('');
      setOtherCourierName('');
      setNoOfDockets(1);
      setTrackingIds(['']);
      setTrackingLinks(['']);
      setErrors({});
      setSubmitSuccess(false);
      handleClose();
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleModalClose}
      centered
      size="md"
      backdrop="static"
      keyboard={!isLoading}
    >
      <Modal.Header closeButton className="modal-header-selfship">
        <Modal.Title className="modal-title-selfship">
          Self Ship - Confirm Shipment
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="modal-body-selfship">
        {submitSuccess && (
          <Alert variant="success" className="text-center success-alert">
            <i className="bi bi-check-circle-fill me-2"></i>
            Tracking Submitted Successfully!
          </Alert>
        )}

        {errors.submit && (
          <Alert variant="danger" dismissible onClose={() => setErrors({ ...errors, submit: null })}>
            {errors.submit}
          </Alert>
        )}

        {errors.general && (
          <Alert variant="warning" dismissible onClose={() => setErrors({ ...errors, general: null })}>
            {errors.general}
          </Alert>
        )}

        {!submitSuccess && (
          <Form onSubmit={handleSubmit}>
            <div className="package-card">
              <div className="package-header">
                <h6 className="package-title">Package 1</h6>
              </div>

              <div className="package-body">

                {/* ── Courier Select ── */}
                <div className="form-group-inline">
                  <Form.Label className="form-label-inline">
                    Courier <span className="text-danger">*</span>
                  </Form.Label>
                  <div className="form-input-wrapper">
                    <Form.Select
                      value={carrier}
                      onChange={(e) => handleCourierChange(e.target.value)}
                      isInvalid={!!errors.carrier}
                      disabled={isLoading}
                      className="form-select-simple"
                    >
                      <option value="">Select Courier</option>
                      {courierListOrder.map((courier) => (
                        <option key={courier.cId} value={courier.value}>
                          {courier.label}
                        </option>
                      ))}
                      <option value={OTHERS_VALUE}>Others</option>
                    </Form.Select>
                    {errors.carrier && (
                      <div className="invalid-feedback d-block">{errors.carrier}</div>
                    )}
                  </div>
                </div>

                {/* ── Other Courier Name (shown only when OTHERS selected) ── */}
                {isOthers && (
                  <div className="form-group-inline">
                    <Form.Label className="form-label-inline">
                      Courier Name <span className="text-danger">*</span>
                    </Form.Label>
                    <div className="form-input-wrapper">
                      <Form.Control
                        type="text"
                        placeholder="Enter courier company name"
                        value={otherCourierName}
                        onChange={(e) => handleOtherCourierNameChange(e.target.value)}
                        isInvalid={!!errors.otherCourierName}
                        disabled={isLoading}
                        className="form-control-simple"
                      />
                      {errors.otherCourierName && (
                        <div className="invalid-feedback d-block">{errors.otherCourierName}</div>
                      )}
                    </div>
                  </div>
                )}

                {/* ── No of Dockets ── */}
                <div className="form-group-inline">
                  <Form.Label className="form-label-inline">
                    No of Docket <span className="text-danger">*</span>
                  </Form.Label>
                  <div className="form-input-wrapper">
                    <Form.Control
                      type="number"
                      min="1"
                      max="20"
                      value={noOfDockets || ''}
                      onChange={(e) => handleNoOfDocketsChange(e.target.value)}
                      isInvalid={!!errors.noOfDockets}
                      disabled={isLoading}
                      className="form-control-simple docket-input"
                    />
                    {errors.noOfDockets && (
                      <div className="invalid-feedback d-block">{errors.noOfDockets}</div>
                    )}
                  </div>
                </div>

                {/* ── Per-Docket: Tracking ID + Tracking Link ── */}
                {noOfDockets > 0 && trackingIds.map((trackingId, index) => (
                  <div key={index} className="docket-entry-block">
                    {noOfDockets > 1 && (
                      <div className="docket-entry-label">Docket {index + 1}</div>
                    )}

                    {/* Tracking ID */}
                    <div className="form-group-inline">
                      <Form.Label className="form-label-inline">
                        Tracking ID{noOfDockets > 1 ? ` ${index + 1}` : ''} <span className="text-danger">*</span>
                      </Form.Label>
                      <div className="form-input-wrapper">
                        <Form.Control
                          type="text"
                          placeholder="Enter tracking ID"
                          value={trackingId}
                          onChange={(e) => handleTrackingIdChange(index, e.target.value)}
                          isInvalid={!!errors[`trackingId_${index}`]}
                          disabled={isLoading}
                          className="form-control-simple"
                        />
                        {errors[`trackingId_${index}`] && (
                          <div className="invalid-feedback d-block">{errors[`trackingId_${index}`]}</div>
                        )}
                      </div>
                    </div>

                    {/* Tracking Link - Only for OTHERS */}
                        {isOthers && (
                          <div className="form-group-inline">
                            <Form.Label className="form-label-inline">
                              Tracking Link{noOfDockets > 1 ? ` ${index + 1}` : ''}
                              <span className="text-danger"> *</span>
                            </Form.Label>
                            <div className="form-input-wrapper">
                              <Form.Control
                                type="url"
                                placeholder="https://track.courier.com/..."
                                value={trackingLinks[index] || ''}
                                onChange={(e) => handleTrackingLinkChange(index, e.target.value)}
                                isInvalid={!!errors[`trackingLink_${index}`]}
                                disabled={isLoading}
                                className="form-control-simple"
                              />
                              {errors[`trackingLink_${index}`] && (
                                <div className="invalid-feedback d-block">{errors[`trackingLink_${index}`]}</div>
                              )}
                            </div>
                          </div>
                        )}
                  </div>
                ))}

              </div>
            </div>

            <div className="modal-actions">
              <Button onClick={handleModalClose} disabled={isLoading} className="btn-cancel">
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading} className="btn-submit">
                {isLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-1" />
                    Submitting...
                  </>
                ) : (
                  'Submit'
                )}
              </Button>
            </div>
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default OrderSelfship;